"use client";

import { useRef, useState, useCallback, useEffect, useMemo } from "react";

import type { Service } from "@/types";
import { buildListItemFromService, ga4ViewItemList } from "@/lib/analytics/ga4-ecommerce";
import { useCurrency } from "@/lib/contexts/CurrencyContext";

import ServiceCard from "@/app/components/PopularServices/components/ServiceCard";

const POPULAR_CAROUSEL_LIST_ID = "popular_carousel";
const POPULAR_CAROUSEL_LIST_NAME = "Popular Services";

// PERFORMANCE: This component uses custom CSS animations, not react-multi-carousel
// The responsive configuration has been removed as we're using custom CSS animations

export default function ServicesCarousel({ services }: { services: Service[] }) {
	const currency = useCurrency();
	const servicesKey = useMemo(() => services.map((s) => s._id).join(","), [services]);

	useEffect(() => {
		if (!services.length) return;
		const items = services.map((svc, i) => buildListItemFromService(svc, currency, i + 1));
		ga4ViewItemList({
			item_list_id: POPULAR_CAROUSEL_LIST_ID,
			item_list_name: POPULAR_CAROUSEL_LIST_NAME,
			items,
		});
	}, [servicesKey, currency, services]);

	const [isPaused, setIsPaused] = useState(false);
	const [isDragging, setIsDragging] = useState(false);
	const [startX, setStartX] = useState(0);
	const [currentTranslate, setCurrentTranslate] = useState(0);
	const [touchStartX, setTouchStartX] = useState(0);
	const [touchStartY, setTouchStartY] = useState(0);
	const [hasMoved, setHasMoved] = useState(false);
	const containerRef = useRef<HTMLDivElement>(null);
	const contentRef = useRef<HTMLDivElement>(null);
	const resumeTimeoutRef = useRef<NodeJS.Timeout | null>(null);
	
	// Duplicate services for seamless infinite loop
	const duplicatedServices = [...services, ...services];
	
	// Handle interaction - pause carousel and set timer to resume after 3 seconds
	const handleInteraction = useCallback(() => {
		setIsPaused(true);
		
		// Clear existing timeout if any
		if (resumeTimeoutRef.current) {
			clearTimeout(resumeTimeoutRef.current);
		}
		
		// Set new timeout to resume after 3 seconds
		resumeTimeoutRef.current = setTimeout(() => {
			setIsPaused(false);
			resumeTimeoutRef.current = null;
		}, 3000);
	}, []);
	
	// Cleanup timeout on unmount
	useEffect(() => {
		return () => {
			if (resumeTimeoutRef.current) {
				clearTimeout(resumeTimeoutRef.current);
			}
		};
	}, []);
	
	// Handle mouse down for dragging
	const handleMouseDown = (e: React.MouseEvent) => {
		setIsDragging(true);
		handleInteraction();
		setStartX(e.clientX);
		if (contentRef.current) {
			const transform = window.getComputedStyle(contentRef.current).transform;
			if (transform !== 'none') {
				const matrix = new DOMMatrix(transform);
				setCurrentTranslate(matrix.m41);
			}
		}
	};
	
	// Handle mouse move for dragging
	const handleMouseMove = (e: React.MouseEvent) => {
		if (!isDragging) return;
		const x = e.clientX;
		const walk = (x - startX) * 1.5;
		if (contentRef.current) {
			contentRef.current.style.transform = `translateX(${currentTranslate + walk}px)`;
		}
	};
	
	// Handle mouse up - end dragging
	const handleMouseUp = () => {
		if (isDragging) {
			setIsDragging(false);
			handleInteraction(); // Reset the 3-second timer
		}
	};
	
	// Handle touch start for mobile dragging
	const handleTouchStart = (e: React.TouchEvent) => {
		const touch = e.touches[0];
		setTouchStartX(touch.clientX);
		setTouchStartY(touch.clientY);
		setHasMoved(false);
		setStartX(touch.clientX);
		handleInteraction();
		if (contentRef.current) {
			const transform = window.getComputedStyle(contentRef.current).transform;
			if (transform !== 'none') {
				const matrix = new DOMMatrix(transform);
				setCurrentTranslate(matrix.m41);
			}
		}
	};
	
	// Handle touch move for mobile dragging
	const handleTouchMove = (e: React.TouchEvent) => {
		if (!touchStartX && !touchStartY) return;

		const touch = e.touches[0];
		const deltaX = Math.abs(touch.clientX - touchStartX);
		const deltaY = Math.abs(touch.clientY - touchStartY);
		const DRAG_THRESHOLD = 10;

		if (deltaX > DRAG_THRESHOLD && deltaX > deltaY) {
			if (!hasMoved) {
				setHasMoved(true);
				setIsDragging(true);
				setIsPaused(true);
			}
			e.preventDefault();
			const x = touch.clientX;
			const walk = (x - startX) * 1.5;
			if (contentRef.current) {
				contentRef.current.style.transform = `translateX(${currentTranslate + walk}px)`;
			}
		}
	};
	
	// Handle touch end - end dragging
	const handleTouchEnd = () => {
		if (!hasMoved) {
			setTouchStartX(0);
			setTouchStartY(0);
			setHasMoved(false);
			return;
		}

		if (isDragging) {
			setIsDragging(false);
			handleInteraction(); // Reset the 3-second timer
		}
		setTouchStartX(0);
		setTouchStartY(0);
		setHasMoved(false);
	};

	return (
		<div className="relative mb-6 overflow-visible">
			<div className="relative isolate h-[calc(21rem-1rem+5rem)] w-full overflow-visible sm:h-[calc(21rem-2rem+5rem)]">
				<div 
					ref={containerRef}
					className="relative z-0 h-full w-full select-none overflow-x-clip overflow-y-visible"
					onMouseDown={handleMouseDown}
					onMouseMove={handleMouseMove}
					onMouseUp={handleMouseUp}
					onMouseLeave={() => {
						setIsDragging(false);
						// Don't resume immediately on mouse leave, let the timer handle it
					}}
					onMouseEnter={handleInteraction}
					onClick={handleInteraction}
					onTouchStart={handleTouchStart}
					onTouchMove={handleTouchMove}
					onTouchEnd={handleTouchEnd}
					style={{ 
						cursor: isDragging ? 'grabbing' : 'grab',
						userSelect: 'none',
						WebkitUserSelect: 'none',
						touchAction: 'pan-y',
					}}
				>
					<div 
						ref={contentRef}
						className="relative z-0 flex h-full items-start gap-4 xl:gap-6 2xl:gap-8"
						style={{
							width: 'max-content',
							animationName: isDragging ? 'none' : 'scrollRight',
							animationDuration: '120s',
							animationTimingFunction: 'linear',
							animationIterationCount: 'infinite',
							animationPlayState: isPaused ? 'paused' : 'running',
						}}
						onDragStart={(e) => e.preventDefault()}
					>
						{duplicatedServices.map((service, k) => (
							<div 
								key={k} 
								className="flex w-80 max-w-full shrink-0 self-start"
								draggable={false}
								onClick={handleInteraction}
								onMouseDown={handleInteraction}
								onTouchStart={handleInteraction}
							>
								<ServiceCard
									service={service}
									listing={{
										itemListId: POPULAR_CAROUSEL_LIST_ID,
										itemListName: POPULAR_CAROUSEL_LIST_NAME,
										index: (k % services.length) + 1,
									}}
								/>
							</div>
						))}
					</div>
				</div>
				{/* Width matches gutter from viewport edge to section text (container px-8, xl/2xl max widths). */}
				<div
					aria-hidden
					className="pointer-events-none absolute -inset-y-px left-0 z-[70] hidden w-[2rem] bg-gradient-to-r from-[var(--color-bg-aqua-mist)] from-0% via-[var(--color-bg-aqua-mist)] via-[92%] to-transparent to-100% sm:block xl:w-[max(2rem,calc((100vw-1280px)/2+2rem))] 2xl:w-[max(2rem,calc((100vw-1536px)/2+2rem))] dark:from-[var(--color-core-deep-midnight)] dark:via-[var(--color-core-deep-midnight)] dark:via-[92%] dark:to-transparent dark:to-100%"
				/>
				<div
					aria-hidden
					className="pointer-events-none absolute -inset-y-px right-0 z-[70] hidden w-[2rem] bg-gradient-to-l from-[var(--color-bg-aqua-mist)] from-0% via-[var(--color-bg-aqua-mist)] via-[92%] to-transparent to-100% sm:block xl:w-[max(2rem,calc((100vw-1280px)/2+2rem))] 2xl:w-[max(2rem,calc((100vw-1536px)/2+2rem))] dark:from-[var(--color-core-deep-midnight)] dark:via-[var(--color-core-deep-midnight)] dark:via-[92%] dark:to-transparent dark:to-100%"
				/>
			</div>
			
			
			<style jsx>{`
				@keyframes scrollRight {
					0% {
						transform: translateX(0);
					}
					100% {
						transform: translateX(-50%);
					}
				}
				
				.scrollbar-hide * {
					-webkit-user-drag: none;
					-khtml-user-drag: none;
					-moz-user-drag: none;
					-o-user-drag: none;
					user-drag: none;
				}
			`}</style>
		</div>
	);
}
